import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { isMuted, play, setMuted, warmUp } from '../../lib/feedback'

/**
 * The locked chrome around a Kid Mode activity.
 *
 * Everything a phone offers a wandering finger is closed off here: no
 * navigation, no long-press menu, no text to select, no rubber-band scroll,
 * and the back gesture is caught and put back. What is left is the activity
 * and one small exit.
 *
 * The exit is the whole design problem. It has to be impossible by accident
 * and trivial on purpose, which rules out both a plain button (she will press
 * it) and a hidden gesture (a tired parent will not remember it). A short hold
 * on a big corner button does both: a toddler taps and lets go, and an adult
 * finds it at a glance and holds it without thinking.
 */

const HOLD_MS = 1200

export function KidShell({
  title,
  step,
  steps,
  onExit,
  children,
}: {
  title?: string
  /** 1-based position in the activity, when the renderer reports one. */
  step?: number
  steps?: number
  onExit: () => void
  children: ReactNode
}) {
  const [held, setHeld] = useState(0)
  const [muted, setMutedState] = useState(isMuted)
  const frame = useRef<number | null>(null)
  const started = useRef(0)

  const stopHold = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = null
    started.current = 0
    setHeld(0)
  }, [])

  const startHold = useCallback(() => {
    warmUp()
    if (frame.current !== null) return
    started.current = Date.now()
    const tick = () => {
      const progress = Math.min(1, (Date.now() - started.current) / HOLD_MS)
      setHeld(progress)
      if (progress >= 1) {
        stopHold()
        play('unlock')
        onExit()
        return
      }
      frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
  }, [onExit, stopHold])

  useEffect(() => stopHold, [stopHold])

  // For the parent handing her the phone: the quiet dot says what it is for
  // a few seconds at the start (and on hover), then gets out of her way.
  const [intro, setIntro] = useState(true)
  useEffect(() => {
    const id = window.setTimeout(() => setIntro(false), 4000)
    return () => window.clearTimeout(id)
  }, [])

  /**
   * Catch the back gesture. A swipe from the screen edge is exactly the kind
   * of thing a toddler does by accident, and it would drop her straight into
   * the parent's data. Re-pushing the entry keeps her here; the deliberate
   * exit path is the hold, which unwinds this on its way out.
   */
  useEffect(() => {
    history.pushState({ kid: true }, '')
    const onPop = () => history.pushState({ kid: true }, '')
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const dots = typeof steps === 'number' && steps > 1 && steps <= 12
  const bar = typeof steps === 'number' && steps > 12
  const done = Math.max(0, Math.min(steps ?? 0, step ?? 0))

  return (
    <div
      className="kid"
      role="application"
      aria-label={title ? `${title}, Kid Mode` : 'Kid Mode'}
      onContextMenu={(e) => e.preventDefault()}
    >
      <header className="kid-bar">
        <button
          className="kid-exit"
          style={{ '--held': held } as React.CSSProperties}
          aria-label="Hold to leave"
          onPointerDown={startHold}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') startHold()
          }}
          onKeyUp={stopHold}
        >
          <svg className="kid-exit-ring" viewBox="0 0 44 44" aria-hidden="true">
            <circle className="kid-exit-track" cx="22" cy="22" r="17" />
            <circle className="kid-exit-fill" cx="22" cy="22" r="17" />
          </svg>
          <span className="kid-exit-dot" aria-hidden="true" />
        </button>

        <span className={`kid-exit-hint${held > 0 || intro ? ' is-shown' : ''}`} aria-hidden="true">
          {held > 0 ? 'Keep holding…' : 'Hold to leave'}
        </span>

        {dots && (
          <div className="kid-dots" role="presentation">
            {Array.from({ length: steps as number }, (_, i) => (
              <span key={i} className={`kid-dot${i < done ? ' is-done' : ''}`} />
            ))}
          </div>
        )}
        {bar && (
          <div className="kid-bar-track" role="presentation">
            <span
              className="kid-bar-fill"
              style={{ '--p': done / (steps as number) } as React.CSSProperties}
            />
          </div>
        )}

        <button
          className="kid-mute"
          aria-label={muted ? 'Turn sounds on' : 'Turn sounds off'}
          aria-pressed={muted}
          onClick={() => {
            const next = !muted
            setMuted(next)
            setMutedState(next)
            if (!next) play('tap')
          }}
        >
          {muted ? <MutedIcon /> : <SoundIcon />}
        </button>
      </header>

      <div className="kid-stage">{children}</div>

      {/* The only text on screen, and only for whoever set the activity up. */}
      {title && <p className="sr-only">{title}</p>}
    </div>
  )
}

const S = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const SoundIcon = () => (
  <svg {...S} width="20" height="20">
    <path d="M11 5L6.5 9H3v6h3.5L11 19z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
)

const MutedIcon = () => (
  <svg {...S} width="20" height="20">
    <path d="M11 5L6.5 9H3v6h3.5L11 19z" />
    <path d="M16 10l5 4M21 10l-5 4" />
  </svg>
)
